/* Premium Cartographie module — orchestrator.

Combines:
- MapLibre GL JS map with custom Benchory style + real OSM/FINESS/Sirene data
- Premium dashboard with animated gauges + radar chart + KPIs
- Layer switcher (toggleable POIs by category)
- Heatmap selector (seniors / medical / commercial)
- Fullscreen mode
- Comparatif vs national average
- Side drawer for establishment details with relation status
*/
import { useState, useEffect, useMemo } from 'react';
import {
  MapPin, Layers, Sparkles, Maximize2, Minimize2, Sun, Moon, Palette,
  Target, Pill, Stethoscope, Save, Tag, X, Plus, RefreshCw, BarChart3,
  Filter, Sliders,
} from 'lucide-react';
import MapLibreView, { TILE_STYLES } from './ecosystem/MapLibreView';
import PremiumDashboard from './ecosystem/PremiumDashboard';
import LayerSwitcher, { DEFAULT_LAYERS, ALL_LAYER_KEYS } from './ecosystem/LayerSwitcher';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RELATION_STATUSES = [
  { key: 'pas_de_relation', label: 'Pas de relation', color: '#94A3B8' },
  { key: 'a_contacter', label: 'A contacter', color: '#3B82F6' },
  { key: 'prescripteur_occasionnel', label: 'Prescripteur occasionnel', color: '#F59E0B' },
  { key: 'prescripteur_regulier', label: 'Prescripteur regulier', color: '#10B981' },
  { key: 'partenariat_formel', label: 'Partenariat formel', color: '#7C3AED' },
  { key: 'sous_contrat_pda', label: 'Sous contrat PDA', color: '#059669' },
];

function EstablishmentDrawer({ est, onClose, onRelationSaved }) {
  const [statut, setStatut] = useState('pas_de_relation');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const token = localStorage.getItem('benchory_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!est) return;
    setSaved(false);
    fetch(`${API_URL}/relations/${est.id}`, { headers }).then(r => r.json()).then(d => {
      setStatut(d.statut || 'pas_de_relation');
      setNotes(d.notes || '');
    });
    // eslint-disable-next-line
  }, [est]);

  if (!est) return null;
  const cs = RELATION_STATUSES.find(s => s.key === statut) || RELATION_STATUSES[0];

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/relations/${est.id}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ statut, notes }),
      });
      if (res.ok) {
        setSaved(true);
        onRelationSaved?.();
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[1100]" data-testid="establishment-drawer">
      <div className="absolute inset-0 bg-black/30 transition-opacity" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl overflow-y-auto benchory-drawer-in">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-start justify-between gap-3 z-10">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase bg-[#1B2A4A] text-white">{est.type}</span>
              {est.source && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 uppercase font-bold">{est.source}</span>
              )}
            </div>
            <h2 className="text-base font-bold text-[#1B2A4A] leading-tight">{est.name}</h2>
            <p className="text-[11px] text-gray-500 mt-1">{est.address}</p>
          </div>
          <button onClick={onClose} data-testid="drawer-close-btn" className="p-1 rounded hover:bg-gray-100">
            <X size={18} className="text-gray-400" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {/* Distance + main numbers */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-black text-[#1B2A4A]">{est.distance_m || '?'}m</p>
              <p className="text-[10px] text-gray-400 uppercase">Distance</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-lg font-black text-[#1B2A4A]">{est.capacity || '\u2014'}</p>
              <p className="text-[10px] text-gray-400 uppercase">Capacit&eacute;</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-sm font-bold text-[#1B2A4A] truncate">{est.code_postal || '\u2014'}</p>
              <p className="text-[10px] text-gray-400 uppercase">CP</p>
            </div>
          </div>

          {/* Identifiers + contact */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
            {est.finess_id && (
              <Row label="FINESS" value={est.finess_id} mono />
            )}
            {est.siret && (
              <Row label="SIRET" value={est.siret} mono />
            )}
            {est.osm_id && (
              <Row label="OSM ID" value={est.osm_id} mono />
            )}
            {est.categorie_label && (
              <Row label="Cat&eacute;gorie" value={est.categorie_label} />
            )}
            {est.phone && (
              <Row label="T&eacute;l&eacute;phone" value={<a href={`tel:${est.phone}`} className="text-[#B8860B] hover:underline">{est.phone}</a>} />
            )}
            {est.website && (
              <Row label="Site" value={<a href={est.website} target="_blank" rel="noreferrer" className="text-[#B8860B] hover:underline truncate inline-block max-w-[200px]">{est.website}</a>} />
            )}
            {est.email && (
              <Row label="Email" value={<a href={`mailto:${est.email}`} className="text-[#B8860B] hover:underline">{est.email}</a>} />
            )}
            {est.opening_hours && (
              <Row label="Horaires" value={<span className="text-[10px]">{est.opening_hours}</span>} />
            )}
            {est.brand && (
              <Row label="Enseigne" value={est.brand} />
            )}
          </div>

          {/* Relation */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Tag size={13} className="text-[#B8860B]" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#1B2A4A]">Statut de relation</h3>
              <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full font-bold text-white" style={{ backgroundColor: cs.color }}>
                {cs.label}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-1.5 mb-3">
              {RELATION_STATUSES.map(s => (
                <button
                  key={s.key}
                  onClick={() => setStatut(s.key)}
                  className={`text-[10px] px-2 py-2 rounded-lg font-bold transition border-2 ${
                    statut === s.key ? 'text-white border-transparent' : 'text-gray-500 border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                  style={statut === s.key ? { backgroundColor: s.color } : {}}
                  data-testid={`relation-status-${s.key}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <textarea
              placeholder="Notes (rdv, contacts, historique...)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="relation-notes"
              className="w-full text-xs border border-gray-200 rounded-lg p-2 focus:outline-none focus:border-[#1B2A4A] resize-none"
              rows={3}
            />
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="relation-save-btn"
              className="mt-2 w-full flex items-center justify-center gap-1 py-2 rounded-lg bg-[#1B2A4A] text-white text-xs font-bold hover:bg-[#0F1E37] disabled:opacity-50"
            >
              {saved ? <><Save size={12} /> Enregistr&eacute;</> : saving ? 'Enregistrement...' : <><Save size={12} /> Enregistrer le statut</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between items-center text-xs gap-2">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className={`text-[#1B2A4A] text-right truncate ${mono ? 'font-mono text-[11px]' : ''}`}>{value}</span>
    </div>
  );
}

export default function EcosystemTab() {
  const [mapData, setMapData] = useState(null);
  const [demographics, setDemographics] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [opportunitiesMeta, setOpportunitiesMeta] = useState(null);
  const [concurrence, setConcurrence] = useState(null);
  const [prescripteurs, setPrescripteurs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [activeLayers, setActiveLayers] = useState(() => {
    try {
      const saved = localStorage.getItem('benchory_eco_layers');
      if (saved) return new Set(JSON.parse(saved));
    } catch (e) { /* ignore */ }
    return new Set(DEFAULT_LAYERS);
  });
  useEffect(() => {
    localStorage.setItem('benchory_eco_layers', JSON.stringify([...activeLayers]));
  }, [activeLayers]);

  const [styleKey, setStyleKey] = useState('voyager');
  const [heatmapLayer, setHeatmapLayer] = useState(null);
  const [heatPoints, setHeatPoints] = useState([]);
  const [fullscreen, setFullscreen] = useState(false);
  const [sideTab, setSideTab] = useState('dashboard');
  const [selectedEst, setSelectedEst] = useState(null);
  const [radius, setRadius] = useState(2000);

  const token = localStorage.getItem('benchory_token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async (r) => {
    setLoading(true);
    try {
      const radiusParam = r ? `?radius=${r}` : '';
      const [mapRes, demoRes, oppRes, conRes, presRes] = await Promise.all([
        fetch(`${API_URL}/ecosystem/map${radiusParam}`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/ecosystem/demographics`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/ecosystem/opportunities`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/ecosystem/concurrence`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
        fetch(`${API_URL}/ecosystem/prescripteurs`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
      ]);
      setMapData(mapRes);
      setDemographics(demoRes);
      setOpportunities(oppRes.opportunities || []);
      setOpportunitiesMeta(oppRes);
      setConcurrence(conRes);
      setPrescripteurs(presRes);
      if (!r && mapRes.radius) setRadius(mapRes.radius);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const refreshIngestion = async () => {
    setRefreshing(true);
    try {
      await fetch(`${API_URL}/ecosystem/refresh-mine`, { method: 'POST', headers });
      // Wait a bit for background ingestion then reload
      setTimeout(() => { load(radius); setRefreshing(false); }, 8000);
    } catch (e) {
      setRefreshing(false);
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  // Heatmap
  useEffect(() => {
    if (!heatmapLayer) {
      setHeatPoints([]);
      return;
    }
    fetch(`${API_URL}/ecosystem/heatmap?layer=${heatmapLayer}`, { headers })
      .then(r => r.json()).then(d => setHeatPoints(d.points || []))
      .catch(e => console.error(e));
    // eslint-disable-next-line
  }, [heatmapLayer]);

  const filteredEstablishments = useMemo(() => {
    if (!mapData?.establishments) return [];
    return mapData.establishments.filter(e => activeLayers.has(e.type));
  }, [mapData, activeLayers]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="ecosystem-loading">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-[#1B2A4A] rounded-full animate-spin" />
      </div>
    );
  }
  if (!mapData) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
        <p className="text-sm text-amber-700">Carte indisponible. V&eacute;rifiez votre adresse dans le profil.</p>
      </div>
    );
  }

  return (
    <div className={`${fullscreen ? 'fixed inset-0 z-[1200] bg-white' : 'space-y-3'}`} data-testid="ecosystem-tab">
      {/* Header bar */}
      <div className={`flex items-center justify-between gap-2 flex-wrap ${fullscreen ? 'p-3 border-b border-gray-100' : ''}`}>
        <div className="flex items-center gap-2 min-w-0">
          <MapPin size={14} className="text-[#1B2A4A] shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-bold text-[#1B2A4A] truncate">{mapData.pharmacy_name || 'Votre officine'}</p>
            <p className="text-[10px] text-gray-400">{mapData.establishments?.length || 0} {"\u00e9tablissements dans un rayon de "}{(radius / 1000).toFixed(1)}km</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button onClick={refreshIngestion} disabled={refreshing} data-testid="refresh-ingestion-btn"
            className="text-[10px] flex items-center gap-1 px-2 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold disabled:opacity-50">
            <RefreshCw size={11} className={refreshing ? 'animate-spin' : ''} /> {refreshing ? 'Maj...' : 'Actualiser'}
          </button>
          {/* Style switcher */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {Object.entries(TILE_STYLES).map(([k, v]) => (
              <button key={k} onClick={() => setStyleKey(k)} data-testid={`style-${k}`}
                className={`text-[10px] px-2 py-1 rounded font-bold ${styleKey === k ? 'bg-white text-[#1B2A4A] shadow-sm' : 'text-gray-500'}`}>
                {k === 'positron' && <Sun size={10} className="inline mr-0.5" />}
                {k === 'voyager' && <Palette size={10} className="inline mr-0.5" />}
                {k === 'dark' && <Moon size={10} className="inline mr-0.5" />}
                {v.name.split(' ')[0]}
              </button>
            ))}
          </div>
          {/* Heatmap selector */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {[
              { k: null, label: 'No HM' },
              { k: 'seniors', label: '+65' },
              { k: 'medical', label: 'M\u00e9d.' },
              { k: 'commercial', label: 'Comm.' },
            ].map(o => (
              <button key={String(o.k)} onClick={() => setHeatmapLayer(o.k)} data-testid={`heatmap-btn-${o.k || 'none'}`}
                className={`text-[10px] px-2 py-1 rounded font-bold ${heatmapLayer === o.k ? 'bg-[#1B2A4A] text-white' : 'text-gray-500 hover:bg-gray-200'}`}>
                {o.label}
              </button>
            ))}
          </div>
          <button onClick={() => setFullscreen(!fullscreen)} data-testid="fullscreen-toggle"
            className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700">
            {fullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
          </button>
        </div>
      </div>

      {/* Main content: map + side panel */}
      <div className={`grid ${fullscreen ? 'grid-cols-1 md:grid-cols-[1fr_380px] flex-1 h-[calc(100vh-60px)]' : 'grid-cols-1 lg:grid-cols-[1fr_360px] gap-3'}`}>
        {/* MAP */}
        <div className={`relative bg-gray-100 ${fullscreen ? 'h-full' : 'rounded-xl overflow-hidden h-[540px]'}`}>
          <MapLibreView
            center={mapData.center}
            radius={radius}
            establishments={filteredEstablishments}
            activeLayers={activeLayers}
            onSelectEstablishment={setSelectedEst}
            styleKey={styleKey}
            showHeatmap={heatmapLayer}
            heatPoints={heatPoints}
            fullscreen={fullscreen}
          />
          {/* Radius slider */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white shadow-xl rounded-full px-4 py-2 flex items-center gap-3 z-[500]" data-testid="radius-slider">
            <Sliders size={12} className="text-[#1B2A4A]" />
            <input type="range" min={500} max={5000} step={100} value={radius}
              onChange={(e) => setRadius(parseInt(e.target.value, 10))}
              onMouseUp={(e) => load(parseInt(e.target.value, 10))}
              onTouchEnd={(e) => load(parseInt(e.target.value, 10))}
              className="w-40 accent-[#1B2A4A]" />
            <span className="text-xs font-bold text-[#1B2A4A] tabular-nums">{(radius / 1000).toFixed(1)} km</span>
          </div>
        </div>

        {/* SIDE PANEL */}
        <div className={`${fullscreen ? 'border-l border-gray-100 overflow-y-auto p-3' : ''}`}>
          {/* Side tab selector */}
          <div className="flex bg-gray-100 p-1 rounded-lg mb-3 overflow-x-auto">
            {[
              { k: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { k: 'layers', label: 'Couches', icon: Layers },
              { k: 'opportunities', label: 'Opp.', icon: Sparkles },
              { k: 'concurrence', label: 'Concurr.', icon: Pill },
              { k: 'prescripteurs', label: 'Prescr.', icon: Stethoscope },
            ].map(t => (
              <button key={t.k} onClick={() => setSideTab(t.k)}
                className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-md text-[10px] font-bold transition whitespace-nowrap ${
                  sideTab === t.k ? 'bg-white text-[#1B2A4A] shadow-sm' : 'text-gray-500'
                }`}
                data-testid={`side-tab-${t.k}`}>
                <t.icon size={11} /> {t.label}
              </button>
            ))}
          </div>

          {sideTab === 'dashboard' && (
            <PremiumDashboard
              summary={mapData.summary}
              concurrence={concurrence}
              prescripteurs={prescripteurs}
              demographics={demographics}
              opportunities={opportunities}
            />
          )}

          {sideTab === 'layers' && (
            <LayerSwitcher
              activeLayers={activeLayers}
              onChange={setActiveLayers}
              counts={mapData.summary?.by_type || {}}
            />
          )}

          {sideTab === 'opportunities' && (
            <OpportunitiesPanel opportunities={opportunities} meta={opportunitiesMeta} />
          )}

          {sideTab === 'concurrence' && <ConcurrencePanel concurrence={concurrence} />}
          {sideTab === 'prescripteurs' && <PrescripteursPanel data={prescripteurs} />}
        </div>
      </div>

      {/* Drawer */}
      {selectedEst && (
        <EstablishmentDrawer
          est={selectedEst}
          onClose={() => setSelectedEst(null)}
          onRelationSaved={() => load(radius)}
        />
      )}
    </div>
  );
}

function OpportunitiesPanel({ opportunities, meta }) {
  const token = localStorage.getItem('benchory_token');
  const headers = { Authorization: `Bearer ${token}` };

  const addAction = async (opp) => {
    try {
      const res = await fetch(`${API_URL}/actions`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: opp.title,
          description: opp.action || opp.description,
          source_module: 'ecosystem',
          source_id: opp.id,
          priority: opp.priority === 'haute' ? 'urgent' : 'recommande',
        }),
      });
      if (res.ok) {
        const d = await res.json();
        alert(d.duplicate ? "D\u00e9j\u00e0 dans Mes actions" : "Ajout\u00e9 \u00e0 Mes actions");
      }
    } catch (e) { console.error(e); }
  };

  return (
    <div className="space-y-3" data-testid="opportunities-panel">
      <div className="bg-[#B8860B]/10 border border-[#B8860B]/30 rounded-xl p-3">
        <p className="text-[10px] uppercase font-bold text-[#B8860B] mb-1">Potentiel CA total estim&eacute;</p>
        <p className="text-2xl font-black text-[#B8860B]">
          {(meta?.total_potential_min_year || 0).toLocaleString('fr-FR')} - {(meta?.total_potential_max_year || 0).toLocaleString('fr-FR')} {'\u20ac/an'}
        </p>
      </div>
      {opportunities.map((o, i) => (
        <div key={o.id || i} className="bg-white rounded-xl border border-gray-100 p-3" data-testid={`opportunity-${o.id || o.type}`}>
          <div className="flex items-start gap-2 mb-1">
            <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase text-white shrink-0" style={{ backgroundColor: o.priority === 'haute' ? '#DC2626' : '#F59E0B' }}>
              {o.priority}
            </span>
            <h4 className="text-xs font-bold text-[#1B2A4A]">{o.title}</h4>
          </div>
          <p className="text-[10px] text-gray-500 mt-1 leading-relaxed">{o.description}</p>
          {(o.potential_min_year || o.potential_max_year) && (
            <p className="text-[10px] text-[#B8860B] font-bold mt-2">
              {(o.potential_min_year || 0).toLocaleString('fr-FR')} - {(o.potential_max_year || 0).toLocaleString('fr-FR')} {'\u20ac/an'}
            </p>
          )}
          {o.action && (
            <p className="text-[10px] text-gray-600 mt-1 italic bg-gray-50 p-2 rounded">{o.action}</p>
          )}
          <button onClick={() => addAction(o)} data-testid={`add-opp-${o.id || o.type}`}
            className="w-full mt-2 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-[#1B2A4A] text-white text-[10px] font-bold hover:bg-[#0F1E37]">
            <Plus size={11} /> Ajouter &agrave; mes actions
          </button>
        </div>
      ))}
    </div>
  );
}

function ConcurrencePanel({ concurrence }) {
  if (!concurrence) return <div className="p-4 text-center text-xs text-gray-400">Chargement...</div>;
  return (
    <div className="space-y-3" data-testid="concurrence-panel">
      <div className="bg-white rounded-xl border border-gray-100 p-3">
        <p className="text-[10px] uppercase font-bold text-gray-400">Niveau de concurrence</p>
        <p className="text-3xl font-black mb-1" style={{
          color: concurrence.density.level === 'saturated' ? '#7F1D1D'
               : concurrence.density.level === 'high' ? '#DC2626'
               : concurrence.density.level === 'medium' ? '#F59E0B'
               : '#10B981',
        }} data-testid="concurrence-level">{concurrence.density.level_label}</p>
        <p className="text-[11px] text-gray-600 leading-relaxed">{concurrence.density.explanation}</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-3">
        <p className="text-[10px] uppercase font-bold text-gray-400 mb-2">Densit&eacute; vs r&eacute;f&eacute;rences</p>
        {[
          { label: 'Votre zone', val: concurrence.density.pharma_per_inhabitant || 0, color: '#1B2A4A' },
          { label: 'D\u00e9partement', val: concurrence.density.departmental_avg, color: '#94A3B8' },
          { label: 'R\u00e9gion', val: concurrence.density.regional_avg, color: '#94A3B8' },
          { label: 'France', val: concurrence.density.national_avg, color: '#94A3B8' },
        ].map((r, i, arr) => {
          const max = Math.max(...arr.map(x => x.val));
          return (
            <div key={r.label} className="mb-2">
              <div className="flex justify-between text-[10px] mb-0.5">
                <span className="text-gray-600">{r.label}</span>
                <span className="font-bold" style={{ color: r.color }}>1 / {r.val.toLocaleString('fr-FR')}</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-2 rounded-full" style={{ width: `${(r.val / max) * 100}%`, backgroundColor: r.color }} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-3">
        <p className="text-[10px] uppercase font-bold text-gray-400 mb-2">{concurrence.count} pharmacies</p>
        <div className="space-y-1.5 max-h-72 overflow-auto">
          {concurrence.pharmacies.map(p => (
            <div key={p.id} className="flex items-start justify-between gap-2 py-1.5 border-b border-gray-50">
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold text-[#1B2A4A] truncate">{p.name}</p>
                <p className="text-[9px] text-gray-400 truncate">{p.address}</p>
                <span className="text-[9px] px-1 py-0.5 rounded bg-gray-100 text-gray-600 font-bold inline-block mt-0.5">{p.groupement}</span>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold shrink-0">{p.distance_m}m</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PrescripteursPanel({ data }) {
  if (!data) return <div className="p-4 text-center text-xs text-gray-400">Chargement...</div>;
  return (
    <div className="space-y-3" data-testid="prescripteurs-panel">
      <div className="bg-white rounded-xl border border-gray-100 p-3">
        <p className="text-[10px] uppercase font-bold text-gray-400">Densit&eacute; m&eacute;dicale</p>
        <p className="text-3xl font-black text-[#1B2A4A]" data-testid="prescripteurs-density">{data.global_density}<span className="text-sm font-bold text-gray-400"> /10k</span></p>
        <p className="text-[11px] text-gray-600 leading-relaxed mt-1">
          {"Moyenne nationale : "}<strong>{data.national_density}</strong>{" \u00b7 "}
          Vous &ecirc;tes &agrave; <strong style={{ color: data.pct_vs_nat >= 100 ? '#10B981' : data.pct_vs_nat >= 80 ? '#F59E0B' : '#DC2626' }}>{data.pct_vs_nat}%</strong>. {data.global_status}.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-3">
        <p className="text-[10px] uppercase font-bold text-gray-400 mb-2">9 sp&eacute;cialit&eacute;s prioritaires</p>
        <table className="w-full text-[10px]">
          <tbody>
            {data.by_specialty.map(s => (
              <tr key={s.key} className="border-t border-gray-50" data-testid={`spec-${s.key}`}>
                <td className="py-1.5 font-bold text-[#1B2A4A]">{s.label}</td>
                <td className="py-1.5 text-center">{s.count}</td>
                <td className="py-1.5 text-right font-bold" style={{ color: s.color === 'green' ? '#10B981' : s.color === 'amber' ? '#F59E0B' : '#DC2626' }}>
                  {s.ratio_pct >= 0 ? '+' : ''}{s.ratio_pct}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-3">
        <p className="text-[10px] uppercase font-bold text-gray-400 mb-2">{data.detailed.length} m&eacute;decins</p>
        <div className="space-y-1.5 max-h-72 overflow-auto">
          {data.detailed.map(m => (
            <div key={m.id} className="flex items-start justify-between gap-2 py-1.5 border-b border-gray-50">
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold text-[#1B2A4A] truncate">{m.name}</p>
                <p className="text-[9px] text-gray-400 truncate">{m.address}</p>
              </div>
              <div className="flex flex-col items-end gap-0.5 shrink-0">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-bold">{m.distance_m}m</span>
                <span className="text-[8px] px-1 py-0.5 rounded font-bold text-white"
                  style={{ backgroundColor: m.potential === 'tres_proche' ? '#DC2626' : m.potential === 'proche' ? '#F59E0B' : m.potential === 'moyen' ? '#3B82F6' : '#94A3B8' }}>
                  {m.potential_label}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
